import { useCallback, useEffect, useState } from "react";
import API, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { HouseLine, Warning, CalendarCheck, ChatCircleText, PaperPlaneTilt } from "@phosphor-icons/react";
import { toast } from "sonner";

const fmt = (iso) => iso ? new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "";
const slotLabel = (s) => s ? `${new Date(s.date).toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "short" })} ${s.period} (${s.period === "AM" ? "8–12" : "12–5"})` : "";
const SEV = { critical: "bg-red-100 text-red-700", urgent: "bg-amber-100 text-amber-800", non_urgent: "bg-blue-100 text-blue-700" };

/** Staff view of the resident's self-triage: answers, suggested vs chosen urgency, slot preferences (confirm one) and the message thread. */
export default function ResidentReportCard({ entityType, entityId }) {
  const [rep, setRep] = useState(undefined);
  const [msg, setMsg] = useState("");
  const [internal, setInternal] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await API.get(`/tenant-reports/by-entity/${entityType}/${entityId}`); setRep(data.report); } catch { setRep(null); }
  }, [entityType, entityId]);
  useEffect(() => { load(); }, [load]);

  if (!rep) return null;
  const triage = rep.triage || {};
  const answers = triage.answers || [];
  const flagged = answers.filter(a => a.severity || a.stop);

  const confirm = async (s) => {
    setBusy(true);
    try { await API.post(`/tenant-reports/${rep.id}/confirm-slot`, { date: s.date, period: s.period }); toast.success("Appointment confirmed — resident emailed"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't confirm"); }
    finally { setBusy(false); }
  };
  const send = async () => {
    if (!msg.trim()) return;
    setBusy(true);
    try { await API.post(`/tenant-reports/${rep.id}/comments`, { message: msg, internal }); setMsg(""); toast.success(internal ? "Internal note saved" : "Reply sent to the resident"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't send"); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 space-y-4" data-testid="resident-report-card">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="text-base font-semibold flex items-center gap-2"><HouseLine className="h-4 w-4" /> Resident report {rep.reference}</h3>
          <p className="text-xs text-muted-foreground">{rep.tenant_name}{rep.tenant_phone ? ` · ${rep.tenant_phone}` : ""}{rep.tenant_email ? ` · ${rep.tenant_email}` : ""} · submitted {fmt(rep.created_at)}</p>
        </div>
        <div className="flex items-center gap-1.5 text-xs">
          <Badge className={`text-[10px] ${SEV[rep.severity] || ""}`} data-testid="resident-chosen-severity">chose {rep.severity?.replace("_", " ")}</Badge>
          {triage.suggested_severity && triage.suggested_severity !== rep.severity && <Badge variant="outline" className="text-[10px]" data-testid="resident-suggested-severity">triage suggested {triage.suggested_severity.replace("_", " ")}</Badge>}
        </div>
      </div>

      {answers.length > 0 && (
        <div data-testid="resident-triage-answers">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Triage answers{flagged.length ? ` · ${flagged.length} flagged` : ""}</p>
          {(triage.stops || []).length > 0 && <p className="text-xs rounded-md border border-red-300 bg-red-50 dark:bg-red-950/30 p-2 mb-2 text-red-800 dark:text-red-200 flex gap-1.5"><Warning className="h-4 w-4 shrink-0" weight="fill" /> Safety stop shown to the resident: {triage.stops.join(" ")}</p>}
          <ul className="grid sm:grid-cols-2 gap-1 text-xs">
            {answers.map(a => (
              <li key={a.key} className={`rounded-md px-2 py-1.5 border ${a.stop ? "border-red-300 bg-red-50/60 dark:bg-red-950/20" : a.severity ? "border-amber-300 bg-amber-50/60 dark:bg-amber-950/20" : "border-border"}`}>
                <span className="text-muted-foreground">{a.text}</span> <strong className="capitalize">{a.answer}</strong>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div data-testid="resident-slots">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1 flex items-center gap-1"><CalendarCheck className="h-3 w-3" /> Appointment</p>
        {rep.confirmed_slot ? (
          <p className="text-sm text-emerald-700 dark:text-emerald-300" data-testid="resident-confirmed-slot">Confirmed: <strong>{slotLabel(rep.confirmed_slot)}</strong> <span className="text-xs text-muted-foreground">by {rep.confirmed_slot.confirmed_by} · {fmt(rep.confirmed_slot.confirmed_at)}</span></p>
        ) : (rep.preferred_slots || []).length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {rep.preferred_slots.map((s, i) => (
              <button key={i} type="button" onClick={() => confirm(s)} disabled={busy} className="text-xs rounded-full border border-border px-2.5 py-1 hover:bg-emerald-50 hover:border-emerald-400 dark:hover:bg-emerald-950/30" data-testid={`resident-slot-confirm-${s.date}-${s.period}`}>{slotLabel(s)} · confirm</button>
            ))}
          </div>
        ) : <p className="text-xs text-muted-foreground">Resident didn't offer preferred slots.</p>}
      </div>

      <div data-testid="resident-thread">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1 flex items-center gap-1"><ChatCircleText className="h-3 w-3" /> Messages</p>
        <div className="space-y-1.5 max-h-60 overflow-y-auto pr-1">
          {(rep.comments || []).length === 0 && <p className="text-xs text-muted-foreground">No messages yet.</p>}
          {(rep.comments || []).map(c => (
            <div key={c.id} className={`rounded-md p-2 text-sm ${c.by === "resident" ? "bg-primary/10" : c.internal ? "bg-amber-50 dark:bg-amber-950/20 border border-dashed border-amber-300" : "bg-muted/50"}`} data-testid={`resident-thread-${c.id}`}>
              <p className="whitespace-pre-wrap">{c.message}</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">{c.by === "resident" ? rep.tenant_name : c.name}{c.internal ? " · internal" : ""} · {fmt(c.at)}</p>
            </div>
          ))}
        </div>
        <div className="mt-2 space-y-1.5">
          <Textarea rows={2} value={msg} onChange={e => setMsg(e.target.value)} placeholder={internal ? "Internal note (resident won't see this)" : "Reply to the resident — they get an email and see it in their portal"} data-testid="resident-reply-input" />
          <div className="flex items-center justify-between">
            <label className="text-xs inline-flex items-center gap-1.5 text-muted-foreground"><input type="checkbox" checked={internal} onChange={e => setInternal(e.target.checked)} data-testid="resident-reply-internal" /> Internal only</label>
            <Button size="sm" onClick={send} disabled={busy || !msg.trim()} data-testid="resident-reply-send"><PaperPlaneTilt className="h-4 w-4 mr-1" /> {internal ? "Save note" : "Send reply"}</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
